import React, { useEffect, useState, createContext, useContext } from 'react';

export const ThemeContext = createContext();

/**
 * ThemeProvider Component
 * Dynamically applies custom branding and white-label themes
 * Also manages System Theme (Studio vs Creative)
 */
const ThemeProvider = ({ settings, children }) => {
    // System Theme State ('studio' | 'creative')
    const [themeMode, setThemeMode] = useState(() => {
        return localStorage.getItem('app_theme_mode') || 'studio';
    });

    // Persist and Apply Theme
    useEffect(() => {
        localStorage.setItem('app_theme_mode', themeMode);
        document.documentElement.setAttribute('data-theme', themeMode);
    }, [themeMode]);

    const toggleTheme = () => {
        setThemeMode(prev => prev === 'studio' ? 'creative' : 'studio');
    };

    // Existing Settings/Branding Logic
    useEffect(() => {
        if (!settings) return;

        // Apply custom CSS variables if theme is available
        if (settings.customTheme && settings.customTheme.colors) {
            const colors = settings.customTheme.colors;
            const root = document.documentElement;

            // Set CSS custom properties
            root.style.setProperty('--brand-primary', colors.primary);
            root.style.setProperty('--brand-secondary', colors.secondary);
            root.style.setProperty('--brand-accent', colors.accent);
            root.style.setProperty('--brand-light', colors.light);
            root.style.setProperty('--brand-dark', colors.dark);
            root.style.setProperty('--brand-gradient-start', colors.gradient.start);
            root.style.setProperty('--brand-gradient-end', colors.gradient.end);
            root.style.setProperty('--brand-text-primary', colors.text.onPrimary);
            root.style.setProperty('--brand-text-secondary', colors.text.onSecondary);

        } else if (settings.brandColor) {
            // Fallback to simple brand color
            const root = document.documentElement;
            root.style.setProperty('--brand-primary', settings.brandColor);
            root.style.setProperty('--brand-secondary', settings.brandColorSecondary || settings.brandColor);
            root.style.setProperty('--brand-accent', settings.brandColorAccent || settings.brandColor);
        }

        // Inject custom CSS if available
        if (settings.customCSS) {
            let styleElement = document.getElementById('custom-brand-styles');
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'custom-brand-styles';
                document.head.appendChild(styleElement);
            }
            styleElement.textContent = settings.customCSS;
        }

        // Update favicon and app title for white-labeling
        if (settings.podcastLogo) {
            updateFavicon(settings.podcastLogo);
        }

        if (settings.podcastName && settings.podcastName !== 'My Podcast') {
            document.title = `${settings.podcastName} - Podcast Management`;
        }

    }, [settings]);

    // Function to update favicon
    const updateFavicon = (logoUrl) => {
        try {
            // Remove existing favicons
            const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
            existingFavicons.forEach(favicon => favicon.remove());

            // Create new favicon
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.type = 'image/x-icon';
            newFavicon.href = logoUrl;
            document.head.appendChild(newFavicon);

            // Also set apple-touch-icon
            const appleFavicon = document.createElement('link');
            appleFavicon.rel = 'apple-touch-icon';
            appleFavicon.href = logoUrl;
            document.head.appendChild(appleFavicon);
        } catch (error) {
        }
    };

    return (
        <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
            <div className="theme-provider text-[var(--text-main)] transition-colors duration-300">
                {children}
            </div>
        </ThemeContext.Provider>
    );
};

export const useAppTheme = () => useContext(ThemeContext);

/**
 * Custom Hook for accessing branding colors (Legacy/Settings based)
 */
export const useTheme = (settings) => {
    if (!settings) {
        return {
            primary: '#6366f1',
            secondary: '#a855f7',
            accent: '#10b981',
            isLightTheme: false
        };
    }

    // ... existing logic ...
    if (settings.customTheme && settings.customTheme.colors) {
        return {
            primary: settings.customTheme.colors.primary,
            secondary: settings.customTheme.colors.secondary,
            accent: settings.customTheme.colors.accent,
            light: settings.customTheme.colors.light,
            dark: settings.customTheme.colors.dark,
            isLightTheme: settings.customTheme.isLightTheme,
            gradient: settings.customTheme.colors.gradient
        };
    }

    return {
        primary: settings.brandColor || '#6366f1',
        secondary: settings.brandColorSecondary || '#a855f7',
        accent: settings.brandColorAccent || '#10b981',
        isLightTheme: settings.isLightTheme !== false
    };
};

/**
 * Component to display brand logo with fallback
 */
export const BrandLogo = ({ settings, className = "", size = "w-8 h-8", fallbackText = "PG" }) => {
    const theme = useTheme(settings);

    if (settings?.podcastLogo) {
        return (
            <img
                src={settings.podcastLogo}
                alt={settings.podcastName || "Logo"}
                className={`${size} object-contain ${className}`}
            />
        );
    }

    // Fallback to SaaS platform logo
    return (
        <img
            src="/logo/logo.png"
            alt="PodGuestly"
            className={`${size} object-contain ${className}`}
            onError={(e) => {
                // If the platform logo fails to load, fall back to initials
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                if (parent) {
                    const fallback = document.createElement('div');
                    fallback.className = `${size} ${className} flex items-center justify-center rounded-lg font-bold text-white`;
                    fallback.style.background = `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`;
                    fallback.innerText = settings?.podcastName
                        ? settings.podcastName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)
                        : fallbackText;
                    parent.appendChild(fallback);
                }
            }}
        />
    );
};

/**
 * Component for applying brand gradients
 */
export const BrandGradient = ({ settings, children, className = "" }) => {
    const theme = useTheme(settings);

    return (
        <div
            className={className}
            style={{
                background: theme.gradient
                    ? `linear-gradient(135deg, ${theme.gradient.start} 0%, ${theme.gradient.end} 100%)`
                    : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`
            }}
        >
            {children}
        </div>
    );
};

export default ThemeProvider;
